import { StyleSheet } from 'aphrodite';
import * as React from 'react';
import { useDeleteNoteMutation } from '../../../queries/Support/useSupportThreadMutations';
import { getApiErrorMessage } from '../../../utils/Support/supportHelpers';
import { TrashIcon } from '../../../web/components/svgs/icons/TrashIcon';
import { Button } from '../../../web/components-v2/Button';
import { THEME } from '../../../web/components-v2/Theme/theme';
import { ConfirmModal } from '../../components/supportShared/ConfirmModal';

interface NoteDeleteActionProps {
	ticketId: string;
	threadId: string;
	/** Called while the dialog is still open: this trigger unmounts with the note, so it cannot take focus back. */
	onDeleted?: () => void;
}

export const NoteDeleteAction: React.FC<NoteDeleteActionProps> = ({ ticketId, threadId, onDeleted }) => {
	const [confirming, setConfirming] = React.useState(false);
	const deleteNote = useDeleteNoteMutation(ticketId);

	const closeConfirm = () => {
		setConfirming(false);
		deleteNote.reset();
	};

	return (
		<>
			<Button
				variant='ghost'
				size='icon'
				styles={[styles.trigger]}
				aria-label='Delete note'
				title='Delete note'
				onClick={() => setConfirming(true)}
			>
				<TrashIcon fillColor='currentColor' />
			</Button>
			{confirming && (
				<ConfirmModal
					title='Delete note'
					message='Delete this note for everyone on the ticket? This cannot be undone.'
					confirmLabel='Delete'
					variant='danger'
					busy={deleteNote.isPending}
					error={
						deleteNote.isError
							? getApiErrorMessage(deleteNote.error, 'Failed to delete the note. Please try again.')
							: undefined
					}
					onConfirm={() =>
						deleteNote.mutate(threadId, {
							onSuccess: () => {
								onDeleted?.();
								setConfirming(false);
							},
						})
					}
					onCancel={closeConfirm}
				/>
			)}
		</>
	);
};

const styles = StyleSheet.create({
	trigger: {
		flex: 'none',
		// The 24px hit area would otherwise make the header taller than the timestamp line it sits beside.
		marginTop: -4,
		marginBottom: -4,
		color: THEME.colors.text.light,
		':hover': {
			color: THEME.colors.danger.main,
		},
	},
});
